import Swal from "sweetalert2";

const swalCustomClasses = {
  confirmButton: "px-6 py-2.5 rounded-xl font-semibold text-white bg-primary-500 hover:bg-primary-700 transition-all duration-200 mx-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2",
  cancelButton: "px-6 py-2.5 rounded-xl font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 transition-all duration-200 mx-2 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2",
  popup: "rounded-[2rem] border border-slate-200 dark:border-slate-800 dark:bg-slate-900 shadow-2xl",
  title: "text-slate-900 dark:text-white font-bold text-xl pt-4",
  htmlContainer: "text-slate-600 dark:text-slate-400 text-sm",
  icon: "mt-4",
};

const swalCustom = Swal.mixin({
  customClass: swalCustomClasses,
  buttonsStyling: false,
});

const alertService = {
  success: (message, title = "Success") => {
    return swalCustom.fire({
      icon: "success",
      title: title,
      text: message,
      iconColor: "#16A34A",
      timer: 3000,
      timerProgressBar: true,
    });
  },
  error: (message, title = "Error") => {
    return swalCustom.fire({
      icon: "error",
      title: title,
      text: message,
      iconColor: "#DC2626",
    });
  },
  warning: (message, title = "Warning") => {
    return swalCustom.fire({
      icon: "warning",
      title: title,
      text: message,
      iconColor: "#F59E0B",
    });
  },
  info: (message, title = "Info") => {
    return swalCustom.fire({
      icon: "info",
      title: title,
      text: message,
      iconColor: "#c98d3e",
    });
  },
  confirm: (message, titleOrOptions = "Are you sure?") => {
    const isOptions = typeof titleOrOptions === "object";
    const title = isOptions ? (titleOrOptions.title || "Are you sure?") : titleOrOptions;
    const extraOptions = isOptions ? titleOrOptions : {};

    return swalCustom.fire({
      icon: "question",
      title: title,
      html: message,
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      ...extraOptions,
    });
  },
  deleteConfirm: (itemName = "this record") => {
    return swalCustom.fire({
      icon: "warning",
      title: "Delete Record",
      text: `Are you sure you want to delete ${itemName}? This action cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "No, keep it",
      iconColor: "#F59E0B",
      reverseButtons: true,
      customClass: {
        ...swalCustomClasses,
        confirmButton:
          "px-6 py-2.5 rounded-xl font-semibold text-white bg-rose-500 hover:bg-rose-600 transition-all duration-200 mx-2 focus:outline-none focus:ring-2 focus:ring-rose-500/20",
      },
    });
  },
  editConfirm: (message = "Are you sure you want to update this record? Changes will be saved.") => {
    return swalCustom.fire({
      icon: "question",
      title: "Confirm Changes",
      text: message,
      showCancelButton: true,
      confirmButtonText: "Yes, update it",
      cancelButtonText: "No, I don't",
      reverseButtons: true,
    });
  },
  confirmPaymentAction: (details, actionType = "save") => {
    let title = "Confirm Payment Details";
    let icon = "question";
    let confirmButtonText = "Confirm";
    let cancelButtonText = "Cancel";
    let iconColor = "#C98A2E"; // brand theme color
    let confirmButtonColorClass = "bg-primary-500 hover:bg-primary-700 focus:ring-primary-500";

    if (actionType === "save") {
      title = "Record Payment";
      confirmButtonText = "Save Payment";
      confirmButtonColorClass = "bg-primary-500 hover:bg-primary-700 focus:ring-primary-500";
      iconColor = "#C98A2E"; // brand theme color
      icon = "question";
    } else if (actionType === "update") {
      title = "Update Payment";
      confirmButtonText = "Update Payment";
      confirmButtonColorClass = "bg-primary-500 hover:bg-primary-700 focus:ring-primary-500";
      iconColor = "#C98A2E"; // brand theme color
      icon = "question";
    } else if (actionType === "delete") {
      title = "Delete Payment";
      confirmButtonText = "Yes, delete it";
      confirmButtonColorClass = "bg-rose-500 hover:bg-rose-600 focus:ring-rose-500";
      iconColor = "#EF4444"; // rose red
      icon = "warning";
    }

    const htmlContent = `
      <div class="text-left mt-4 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 space-y-3 font-sans">
        <div class="flex justify-between border-b border-slate-200/60 dark:border-slate-800 pb-2">
          <span class="font-bold text-slate-400 text-[11px] uppercase tracking-wider">Date</span>
          <span class="font-semibold text-slate-800 dark:text-slate-200 text-xs">${details.date}</span>
        </div>
        <div class="flex justify-between border-b border-slate-200/60 dark:border-slate-800 pb-2">
          <span class="font-bold text-slate-400 text-[11px] uppercase tracking-wider">Payment Mode</span>
          <span class="font-bold text-primary-600 dark:text-primary-400 text-xs">${details.paymentMode}</span>
        </div>
        <div class="flex justify-between border-b border-slate-200/60 dark:border-slate-800 pb-2">
          <span class="font-bold text-slate-400 text-[11px] uppercase tracking-wider">Amount</span>
          <span class="font-extrabold text-slate-900 dark:text-white text-sm font-mono">${details.formattedAmount}</span>
        </div>
        ${details.gstAmount ? `
        <div class="flex justify-between border-b border-slate-200/60 dark:border-slate-800 pb-2">
          <span class="font-bold text-slate-400 text-[11px] uppercase tracking-wider">GST Amount</span>
          <span class="font-bold text-slate-700 dark:text-slate-300 text-xs font-mono">${details.gstAmount}</span>
        </div>` : ''}
        ${details.tdsAmount ? `
        <div class="flex justify-between border-b border-slate-200/60 dark:border-slate-800 pb-2">
          <span class="font-bold text-slate-400 text-[11px] uppercase tracking-wider">TDS Amount</span>
          <span class="font-bold text-slate-700 dark:text-slate-300 text-xs font-mono">${details.tdsAmount}</span>
        </div>` : ''}
        ${details.netAmount ? `
        <div class="flex justify-between border-b border-slate-200/60 dark:border-slate-800 pb-2">
          <span class="font-bold text-slate-400 text-[11px] uppercase tracking-wider">Net Amount</span>
          <span class="font-bold text-slate-900 dark:text-white text-xs font-mono">${details.netAmount}</span>
        </div>` : ''}
        ${details.currencyRate ? `
        <div class="flex justify-between border-b border-slate-200/60 dark:border-slate-800 pb-2">
          <span class="font-bold text-slate-400 text-[11px] uppercase tracking-wider">Rate</span>
          <span class="font-semibold text-slate-600 dark:text-slate-400 text-xs font-mono">${details.currencyRate}</span>
        </div>` : ''}
        ${details.amountInRs ? `
        <div class="flex justify-between border-b border-slate-200/60 dark:border-slate-800 pb-2">
          <span class="font-bold text-slate-400 text-[11px] uppercase tracking-wider">Amount (₹)</span>
          <span class="font-extrabold text-emerald-600 dark:text-emerald-400 text-xs font-mono">${details.amountInRs}</span>
        </div>` : ''}
        ${details.paymentDetails ? `
        <div class="flex flex-col gap-1 border-b border-slate-200/60 dark:border-slate-800 pb-2">
          <span class="font-bold text-slate-400 text-[11px] uppercase tracking-wider">Payment Details</span>
          <div class="text-xs text-slate-700 dark:text-slate-300 font-medium bg-white dark:bg-slate-800 p-2 rounded-lg border border-slate-200/60 dark:border-slate-800 mt-1 space-y-1">${details.paymentDetails}</div>
        </div>` : ''}
        ${details.remarks ? `
        <div class="flex flex-col gap-1">
          <span class="font-bold text-slate-400 text-[11px] uppercase tracking-wider">Remarks</span>
          <span class="text-xs text-slate-500 italic mt-1 max-h-[80px] overflow-y-auto custom-scrollbar block whitespace-pre-wrap">${details.remarks}</span>
        </div>` : ''}
      </div>
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-4 text-center font-medium">
        ${actionType === 'delete' 
          ? 'Are you sure you want to delete this payment transaction? This action cannot be undone.'
          : `Are you sure you want to ${actionType} this payment transaction?`}
      </p>
    `;

    return swalCustom.fire({
      icon: icon,
      title: title,
      html: htmlContent,
      showCancelButton: true,
      confirmButtonText: confirmButtonText,
      cancelButtonText: cancelButtonText,
      iconColor: iconColor,
      reverseButtons: true,
      customClass: {
        ...swalCustomClasses,
        confirmButton: `px-6 py-2.5 rounded-xl font-semibold text-white ${confirmButtonColorClass} transition-all duration-200 mx-2 focus:outline-none focus:ring-2 focus:ring-offset-2`,
      }
    });
  }
};

export default alertService;
